import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

export interface FloatPosition {
  x: number;
  y: number;
}

export interface FloatSize {
  w: number;
  h: number;
}

interface UseMovableResizableOptions {
  movable: boolean;
  resizable: boolean;
  isOpen: boolean;
  minSize?: FloatSize;
}

interface UseMovableResizableResult {
  isFloating: boolean;
  pos: FloatPosition;
  size: FloatSize;
  panelRef: RefObject<HTMLDivElement | null>;
  headerRef: (node: HTMLElement | null) => void;
  gripRef: (node: HTMLDivElement | null) => void;
}

const DEFAULT_MIN_SIZE: FloatSize = { w: 280, h: 200 };

/**
 * Drives drag-to-move (via headerRef) and drag-to-resize (via gripRef) for an
 * element that otherwise sizes/positions itself with CSS. Adapted from the
 * FloatPanel drag/resize wiring in src/blc/SidebarComponents.jsx.
 *
 * The element stays CSS-positioned (isFloating === false) until the user
 * first drags the header or grip, at which point the current on-screen rect
 * (read off panelRef) becomes the starting pos/size and the caller is
 * expected to switch to inline fixed positioning.
 *
 * headerRef/gripRef are callback refs (backed by state, not useRef) because
 * HeadlessUI's Dialog unmounts its content on close and remounts it on
 * reopen — a plain ref set up once on mount would go stale after the first
 * close/reopen cycle.
 */
export function useMovableResizable({
  movable,
  resizable,
  isOpen,
  minSize = DEFAULT_MIN_SIZE,
}: UseMovableResizableOptions): UseMovableResizableResult {
  const [isFloating, setIsFloating] = useState(false);
  const [pos, setPos] = useState<FloatPosition>({ x: 0, y: 0 });
  const [size, setSize] = useState<FloatSize>({ w: 0, h: 0 });

  const panelRef = useRef<HTMLDivElement>(null);
  const [headerEl, setHeaderEl] = useState<HTMLElement | null>(null);
  const [gripEl, setGripEl] = useState<HTMLDivElement | null>(null);
  const headerRef = useCallback((node: HTMLElement | null) => setHeaderEl(node), []);
  const gripRef = useCallback((node: HTMLDivElement | null) => setGripEl(node), []);

  const isFloatingRef = useRef(isFloating);
  const posRef = useRef(pos);
  const sizeRef = useRef(size);
  const movableRef = useRef(movable);
  const resizableRef = useRef(resizable);
  const minSizeRef = useRef(minSize);
  useEffect(() => {
    isFloatingRef.current = isFloating;
  }, [isFloating]);
  useEffect(() => {
    posRef.current = pos;
  }, [pos]);
  useEffect(() => {
    sizeRef.current = size;
  }, [size]);
  useEffect(() => {
    movableRef.current = movable;
  }, [movable]);
  useEffect(() => {
    resizableRef.current = resizable;
  }, [resizable]);
  useEffect(() => {
    minSizeRef.current = minSize;
  }, [minSize]);

  // Reset to the default CSS-driven layout every time the modal (re)opens.
  const wasOpenRef = useRef(isOpen);
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) setIsFloating(false);
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    if (!headerEl && !gripEl) return;

    let dragging = false;
    let resizing = false;
    let startX = 0;
    let startY = 0;
    let startLeft = 0;
    let startTop = 0;
    let startW = 0;
    let startH = 0;

    // Where the panel currently is: its live rect if still CSS-positioned,
    // or the tracked floating pos/size once it's been picked up once.
    const currentRect = () => {
      if (isFloatingRef.current) {
        return { left: posRef.current.x, top: posRef.current.y, w: sizeRef.current.w, h: sizeRef.current.h };
      }
      const rect = panelRef.current?.getBoundingClientRect();
      return {
        left: rect?.left ?? 0,
        top: rect?.top ?? 0,
        w: rect?.width ?? minSizeRef.current.w,
        h: rect?.height ?? minSizeRef.current.h,
      };
    };

    const beginFloatingIfNeeded = (rect: { left: number; top: number; w: number; h: number }) => {
      if (isFloatingRef.current) return;
      isFloatingRef.current = true;
      setIsFloating(true);
      setPos({ x: rect.left, y: rect.top });
      setSize({ w: rect.w, h: rect.h });
    };

    const onHdrDown = (e: MouseEvent) => {
      if (!movableRef.current) return;
      if ((e.target as HTMLElement).closest('[data-modal-btn]')) return;
      const rect = currentRect();
      beginFloatingIfNeeded(rect);
      dragging = true;
      startX = e.clientX;
      startY = e.clientY;
      startLeft = rect.left;
      startTop = rect.top;
      e.preventDefault();
    };

    const onGripDown = (e: MouseEvent) => {
      if (!resizableRef.current) return;
      const rect = currentRect();
      beginFloatingIfNeeded(rect);
      resizing = true;
      startX = e.clientX;
      startY = e.clientY;
      startW = rect.w;
      startH = rect.h;
      e.preventDefault();
    };

    const onMove = (e: MouseEvent) => {
      if (dragging) {
        setPos({ x: startLeft + (e.clientX - startX), y: startTop + (e.clientY - startY) });
      }
      if (resizing) {
        setSize({
          w: Math.max(minSizeRef.current.w, startW + (e.clientX - startX)),
          h: Math.max(minSizeRef.current.h, startH + (e.clientY - startY)),
        });
      }
    };

    const onUp = () => {
      dragging = false;
      resizing = false;
    };

    headerEl?.addEventListener('mousedown', onHdrDown);
    gripEl?.addEventListener('mousedown', onGripDown);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      headerEl?.removeEventListener('mousedown', onHdrDown);
      gripEl?.removeEventListener('mousedown', onGripDown);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [headerEl, gripEl]);

  return { isFloating, pos, size, panelRef, headerRef, gripRef };
}
